import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import {
  LoginPageWrapper,
  LoginContainer,
  LoginCard,
  LoginHeader,
  LoginTitle,
  LoginSubtitle,
  FooterText,
  SignUpButton
} from '../../styles/auth/Login.styles';
import { LoginForm, SocialLogin } from '../../components/auth';
import { useAuthContext } from '../../providers';
import { logger } from '../../utils/logger';
import { useSEO } from '../../hooks';

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login, socialLogin } = useAuthContext();
  const [loading, setLoading] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState('');
  const [error, setError] = useState('');

  useSEO({
    title: 'Log In | Plenty of Hearts',
    description: 'Log in to Plenty of Hearts to continue matching, chatting, and playing games with people who share your interests.',
    canonical: '/login',
  });

  // Prevent body scrolling on login page
  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, []);

  const handleEmailLogin = async (email: string, password: string) => {
    setLoading(true);
    setError('');

    try {
      const result = await login(email, password);

      if (result.success) {
        if (result.requiresEmailVerification) {
          // Show email verification message instead of navigating
          setError(result.message || 'Please verify your email to complete login.');
        } else {
          // Route into the app, not straight to onboarding. The onboarding
          // gate (useRoleBasedNavigation) is the single source of truth — it
          // will send the user to /onboarding only if their account hasn't
          // completed it, so finished users land in the app as expected.
          navigate('/social-feed');
        }
      } else {
        // Handle specific error types with better messaging
        if (result.error === 'ACCOUNT_LOCKED') {
          const timeUntilUnlock = result.timeUntilUnlock;
          const hours = Math.floor(timeUntilUnlock! / 60);
          const minutes = timeUntilUnlock! % 60;
          const timeString = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;

          setError(`Account is locked due to too many failed attempts. Please try again in ${timeString}.`);
        } else if (result.error === 'INVALID_CREDENTIALS') {
          if (result.remainingAttempts !== undefined && result.remainingAttempts > 0) {
            setError(`Invalid credentials. ${result.remainingAttempts} attempts remaining.`);
          } else {
            setError('Invalid credentials. Please check your email and password.');
          }
        } else {
          setError(result.message || result.error || 'Login failed');
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };
  /*
  const handleGoogleLogin = async () => {
    setLoading(true);
    setError('');

    try {
      const result = await socialLogin('google');

      if (result.success && result.data?.url) {
        window.location.href = result.data.url;
      } else {
        setError(result.error || 'Google login failed');
        setLoading(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Google login failed');
      setLoading(false);
    }
  };
  */

  const handleForgotPassword = async (email: string) => {
    setForgotPasswordLoading(true);
    setForgotPasswordMessage('');
    setError('');

    // Client-side rate limiting to prevent hitting Supabase limits
    const lastResetKey = `lastPasswordReset_${email}`;
    const lastReset = localStorage.getItem(lastResetKey);
    const now = Date.now();
    const cooldownPeriod = 2 * 60 * 1000; // 2 minutes cooldown

    if (lastReset && (now - parseInt(lastReset)) < cooldownPeriod) {
      const remainingTime = Math.ceil((cooldownPeriod - (now - parseInt(lastReset))) / 1000);
      setError(`Please wait ${remainingTime} seconds before requesting another password reset email.`);
      setForgotPasswordLoading(false);
      return;
    }

    try {
      // Add timeout to prevent hanging requests
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Request timeout - please try again')), 30000); // 30 second timeout
      });

      const resetPromise = supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      });

      const { error } = await Promise.race([resetPromise, timeoutPromise]) as any;

      if (error) {
        // Handle different types of errors
        if (error.message.includes('timeout') || error.message.includes('504')) {
          setError('Password reset service is temporarily unavailable. Please try again in a few minutes.');
        } else if (error.message.includes('Too Many Requests') || error.message.includes('429')) {
          setError('Too many password reset requests. Please wait a few minutes before trying again.');
        } else if (error.message.includes('Invalid email')) {
          setError('Please enter a valid email address.');
        } else {
          setError(error.message || 'Failed to send password reset email');
        }
      } else {
        // Success - store timestamp for rate limiting
        localStorage.setItem(lastResetKey, now.toString());
        setForgotPasswordMessage('Password reset email sent! Check your inbox.');
      }
    } catch (err) {
      console.error('Forgot password error:', err);
      if (err instanceof Error && err.message.includes('timeout')) {
        setError('Request timed out. Please check your connection and try again.');
      } else {
        setError('Failed to send password reset email. Please try again.');
      }
    } finally {
      setForgotPasswordLoading(false);
    }
  };


  return (
    <LoginPageWrapper>
      <LoginContainer>
        <LoginCard>
          <LoginHeader>
            <LoginTitle>Welcome Back</LoginTitle>
            <LoginSubtitle>Sign in to your account to continue</LoginSubtitle>
          </LoginHeader>

          <LoginForm
            onSubmit={handleEmailLogin}
            loading={loading}
            error={error}
            onForgotPassword={handleForgotPassword}
            forgotPasswordLoading={forgotPasswordLoading}
            forgotPasswordMessage={forgotPasswordMessage}
          />

          {/* 
          <SocialLogin
            onGoogleLogin={handleGoogleLogin}
            loading={loading}
          /> 
          */}

          <FooterText>
            Don't have an account? <SignUpButton onClick={() => navigate('/registration')}>Sign up here</SignUpButton>
          </FooterText>
        </LoginCard>
      </LoginContainer>
    </LoginPageWrapper>
  );
};
